import Swal from 'sweetalert2';
import { importTemplateFromFile } from '~src/templates';
import {
  fetchEditorTemplateById,
  createEditorCanvas,
  createEditorCanvasPage,
  updateEditorCanvas,
} from '~graphql/controllers';
import { router } from '~src/router';
import { startAiLoader, stopAiLoader } from '~src/~core/aiLoader';
import { trackEvent } from '~src/tracking/trackEvent';
import {
  getPublisherImportProgressTiming,
  PUBLISHER_IMPORT_LOADING_MESSAGES,
} from '../loader-utils/publisherImportProgress';
import {
  fetchTemplateCategoriesForProjectType,
  getTemplateCategoryPayload,
} from '../loader-utils/publisherImportCategories';
import {
  addFormatPointer,
  buildClosestFormatOptions,
  formatDimensionsLabel,
  getClosestFormatByIndex,
  getDetectedFormatDimensions,
} from '../loader-utils/publisherImportFormatSuggestions';
import { uploadPublisherImportFile } from '../loader-utils/uploadPublisherImportFile';
import { isLoggedUserSuperUser } from '~src/user';

const promptForClosestFormat = async (error) => {
  const closestFormats = error.details?.closestFormats || [];
  const inputOptions = buildClosestFormatOptions(closestFormats);
  const optionKeys = Object.keys(inputOptions);

  if (optionKeys.length === 0) return null;

  const detectedDimensions = formatDimensionsLabel(
    getDetectedFormatDimensions(error.details)
  );

  const result = await Swal.fire({
    title: 'Formato non trovato',
    html: `
      <p>
        Il file misura <strong>${detectedDimensions}</strong>, ma non esiste
        un formato Giotto con queste dimensioni.
      </p>
      <p class="mb-0">
        Puoi continuare adattandolo a uno dei formati più vicini.
      </p>
    `,
    input: 'select',
    inputOptions,
    inputValue: optionKeys[0],
    confirmButtonText: 'Usa questo formato',
    cancelButtonText: 'Annulla conversione',
    showCancelButton: true,
    confirmButtonColor: '#278e8e',
    inputValidator: (value) => {
      if (value === undefined || value === '') return 'Scegli un formato';
    },
  });

  if (!result.isConfirmed) return null;

  return getClosestFormatByIndex(closestFormats, result.value);
};

export const importPublisherFromStart = async (event) => {
  // Tracking: derive which CTA opened the wizard (empty vs populated state)
  const entryPoint =
    event?.currentTarget?.id === 'btn-import-publisher-new'
      ? 'populated'
      : 'empty';
  const MAX_FILE_SIZE = 100 * 1024 * 1024;
  const isSuperUser = await isLoggedUserSuperUser();

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Step 1: File picker
  const pickerResult = await Swal.fire({
    title: 'Converti il tuo file',
    html: `
      <div class="publisher-import-upload">
        <label class="publisher-import-dropzone" tabindex="0">
          <input
            type="file"
            accept=".pub,.cdr"
            class="publisher-import-dropzone-input"
            aria-label="Scegli un file .pub o .cdr"
          />
          <div class="publisher-import-dropzone-icon">
            <i class="fas fa-cloud-upload-alt"></i>
          </div>
          <div class="publisher-import-dropzone-text">
            <strong>Clicca per scegliere il file da convertire</strong>
            <small>oppure trascinalo in questa area</small>
          </div>
        </label>
        <div class="publisher-import-file-selected" hidden>
          <div class="publisher-import-file-icon">
            <i class="fas fa-file-alt"></i>
          </div>
          <div class="publisher-import-file-info">
            <span class="publisher-import-file-name"></span>
            <span class="publisher-import-file-size"></span>
          </div>
          <button
            type="button"
            class="publisher-import-file-remove"
            aria-label="Rimuovi file"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="publisher-import-formats">
          Formati accettati: <strong>.pub</strong> (Microsoft Publisher) e
          <strong>.cdr</strong> (CorelDraw) · dimensione massima
          <strong>100 MB</strong>
        </div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Continua',
    cancelButtonText: 'Annulla',
    confirmButtonColor: '#278e8e',
    focusConfirm: false,
    didOpen: () => {
      const dropzone = document.querySelector('.publisher-import-dropzone');
      const input = dropzone.querySelector('.publisher-import-dropzone-input');
      const selected = document.querySelector(
        '.publisher-import-file-selected'
      );
      const nameEl = selected.querySelector('.publisher-import-file-name');
      const sizeEl = selected.querySelector('.publisher-import-file-size');
      const removeBtn = selected.querySelector('.publisher-import-file-remove');

      const showSelected = (f) => {
        nameEl.textContent = f.name;
        sizeEl.textContent = formatBytes(f.size);
        dropzone.hidden = true;
        selected.hidden = false;
      };

      const clearSelected = () => {
        input.value = '';
        dropzone.hidden = false;
        selected.hidden = true;
      };

      input.addEventListener('change', (e) => {
        const f = e.target.files[0];
        if (f) showSelected(f);
      });

      removeBtn.addEventListener('click', clearSelected);

      dropzone.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          input.click();
        }
      });

      ['dragover', 'dragenter'].forEach((evt) => {
        dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          dropzone.classList.add('is-dragging');
        });
      });

      ['dragleave', 'dragend', 'drop'].forEach((evt) => {
        dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          dropzone.classList.remove('is-dragging');
        });
      });

      dropzone.addEventListener('drop', (e) => {
        const f = e.dataTransfer?.files?.[0];
        if (!f) return;
        const dt = new DataTransfer();
        dt.items.add(f);
        input.files = dt.files;
        showSelected(f);
      });
    },
    preConfirm: () => {
      const input = document.querySelector('.publisher-import-dropzone-input');
      const f = input?.files?.[0];
      if (!f) {
        Swal.showValidationMessage('Scegli un file .pub o .cdr');
        return false;
      }
      const name = f.name.toLowerCase();
      if (!name.endsWith('.pub') && !name.endsWith('.cdr')) {
        Swal.showValidationMessage('Il file deve avere estensione .pub o .cdr');
        return false;
      }
      if (f.size > MAX_FILE_SIZE) {
        Swal.showValidationMessage('Il file è troppo grande (massimo 100 MB)');
        return false;
      }

      return { file: f };
    },
  });

  const file = pickerResult.value?.file;
  if (!file) return;
  const shouldCreateProject = !isSuperUser;

  const fileExt = file.name.split('.').pop().toLowerCase();
  const fileSizeKB = Math.round(file.size / 1024);

  trackEvent('Publisher Import Started', {
    entryPoint,
    fileExt,
    fileSizeKB,
    createProject: shouldCreateProject,
    isSuperUser,
  });

  // Step 2: Select project type
  // Project types are stored in window.editorLoader.projects.items after renderLoader
  const projectItems = window.editorLoader?.projects?.items || [];

  const typeOptions = {};
  for (const pt of projectItems) {
    typeOptions[pt.objectId] = pt.name;
  }

  if (Object.keys(typeOptions).length === 0) {
    Swal.fire({
      title: 'Errore',
      text: 'Nessuna tipologia di progetto disponibile',
      icon: 'error',
      confirmButtonColor: '#278e8e',
    });
    return;
  }

  const { value: projectTypeId } = await Swal.fire({
    title: 'Tipologia del modello',
    text:
      'Scegli dove inserire il modello convertito. Potrai usarlo per creare progetti di questa tipologia.',
    input: 'select',
    inputOptions: typeOptions,
    inputPlaceholder: 'Scegli una tipologia...',
    confirmButtonText: 'Continua',
    cancelButtonText: 'Annulla',
    showCancelButton: true,
    confirmButtonColor: '#278e8e',
    inputValidator: (value) => {
      if (!value) return 'Scegli una tipologia di modello';
    },
  });

  if (!projectTypeId) return;
  const selectedProjectType = projectItems.find(
    (pt) => pt.objectId === projectTypeId
  );

  // Step 3: Select category. Publisher imports must be attached to a
  // template category, otherwise the sidebar cannot render them.
  let categories = [];
  try {
    categories = await fetchTemplateCategoriesForProjectType(
      selectedProjectType
    );
  } catch (error) {
    console.error('Could not load template categories for import:', error);
  }

  if (categories.length === 0) {
    Swal.fire({
      title: 'Errore',
      text: 'Nessuna categoria modello disponibile per questa tipologia.',
      icon: 'error',
      confirmButtonColor: '#278e8e',
    });
    return;
  }

  let selectedCategoryId = categories[0].objectId;

  if (categories.length > 1) {
    const catOptions = {};
    for (const cat of categories) {
      catOptions[cat.objectId] = cat.name;
    }

    const categoryResult = await Swal.fire({
      title: 'Categoria del modello',
      text: 'In quale categoria vuoi organizzare il modello?',
      input: 'select',
      inputOptions: catOptions,
      inputPlaceholder: 'Scegli una categoria...',
      confirmButtonText: 'Converti',
      cancelButtonText: 'Annulla',
      showCancelButton: true,
      confirmButtonColor: '#278e8e',
      inputValidator: (value) => {
        if (!value) return 'Scegli una categoria';
      },
    });

    selectedCategoryId = categoryResult.value;
  }

  if (!selectedCategoryId) return;

  const selectedCategory = categories.find(
    (category) => category.objectId === selectedCategoryId
  );
  const categoryPayload = getTemplateCategoryPayload(
    selectedCategory,
    selectedCategoryId
  );

  // Step 4: Upload + conversion (animated progress loader)
  const conversionStart = performance.now();
  const conversionProgressTiming = getPublisherImportProgressTiming(file.size);
  startAiLoader(
    PUBLISHER_IMPORT_LOADING_MESSAGES,
    '✨ Conversione in corso',
    conversionProgressTiming.durationMs,
    {
      messageCycleDurationMs: conversionProgressTiming.messageCycleDurationMs,
      progressTargetPercent: conversionProgressTiming.progressTargetPercent,
    }
  );

  let selectedFormatFallback = null;

  try {
    let fileUrl;
    const importWithFormat = async (formatOverride = null) => {
      // Upload only once. If the first import only fails on format matching,
      // the retry can reuse the same S3 object.
      if (!fileUrl) {
        fileUrl = await uploadPublisherImportFile(file);
      }

      return importTemplateFromFile({
        sourceFile: {
          url: fileUrl,
          name: file.name,
          sizeBytes: file.size,
        },
        projectTypeId,
        projectTypeIsDefault: selectedProjectType?.isGlobal === true,
        ...categoryPayload,
        name: file.name.replace(/\.[^.]+$/, ''),
        ...(formatOverride
          ? {
              formatId: formatOverride.formatId,
              formatIsDefault: formatOverride.isDefault !== false,
            }
          : {}),
      });
    };

    let result;
    try {
      // Call Lambda API (Flow A - create new template)
      result = await importWithFormat();
    } catch (err) {
      if (err.code !== 'no_format_match') {
        throw err;
      }

      const closestFormats = err.details?.closestFormats || [];
      if (closestFormats.length === 0) {
        throw err;
      }

      stopAiLoader();
      selectedFormatFallback = await promptForClosestFormat(err);

      if (!selectedFormatFallback) {
        trackEvent('Publisher Import Failed', {
          entryPoint,
          fileExt,
          fileSizeKB,
          errorCode: 'no_format_match',
          closestFormatsCount: closestFormats.length,
          userCancelledAfterFormatSuggestions: true,
          estimatedProgressDurationMs: conversionProgressTiming.durationMs,
          durationMs: Math.round(performance.now() - conversionStart),
        });
        return;
      }

      startAiLoader(
        PUBLISHER_IMPORT_LOADING_MESSAGES,
        '✨ Conversione in corso',
        conversionProgressTiming.durationMs,
        {
          messageCycleDurationMs:
            conversionProgressTiming.messageCycleDurationMs,
          progressTargetPercent: conversionProgressTiming.progressTargetPercent,
        }
      );
      result = await importWithFormat(selectedFormatFallback);
    }

    let canvasId = null;

    if (shouldCreateProject) {
      // Step 5: Create a project (editor_canvas) from the imported template
      // Fetch the full template with its pages via GraphQL
      const template = await fetchEditorTemplateById(
        result.templateId,
        false // publisher-imported templates are custom (not global)
      );

      const templatePages = template.editor_templates_pages || [];
      const sortedPages = [...templatePages].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );

      // Determine project type pointer field based on isGlobal flag
      const projectType = (window.editorLoader?.projects?.items || []).find(
        (pt) => pt.objectId === projectTypeId
      );

      const canvasFields = {};
      if (projectType && projectType.isGlobal) {
        canvasFields.editor_project_types_default_pointer = {
          link: projectTypeId,
        };
      } else {
        canvasFields.editor_project_types_pointer = { link: projectTypeId };
      }

      addFormatPointer(canvasFields, result);

      // Link template
      canvasFields.editor_templates_pointer = { link: result.templateId };

      // Metadata
      canvasFields.metadata = {
        formatName: result.formatName || '',
        projectTypeName: projectType?.name || '',
      };

      const canvas = await createEditorCanvas(canvasFields);
      canvasId = canvas.objectId;

      // Create editor_canvas_pages from the template pages
      const canvasPagesIds = [];
      for (const tPage of sortedPages) {
        const pageFields = {
          canvasJson:
            tPage.canvasJson ||
            JSON.stringify({ objects: [], background: '#ffffff' }),
          order: tPage.order || 0,
          editor_canvas_pointer: { link: canvasId },
        };
        addFormatPointer(pageFields, result);
        const page = await createEditorCanvasPage(pageFields);
        canvasPagesIds.push(page.objectId);
      }

      // Create the canvas <-> pages relation
      await updateEditorCanvas(canvasId, {
        editor_canvas_pages: { add: canvasPagesIds },
      });
    }

    // Step 6: Success message
    stopAiLoader();
    const pagesLabel =
      result.pagesCount === 1 ? 'una pagina' : `${result.pagesCount} pagine`;
    const successActionText = shouldCreateProject
      ? 'Apri il progetto'
      : 'Modifica Modello';
    const successRedirectText = shouldCreateProject
      ? 'Apri il progetto per sistemarli — il modello è anche in libreria per usi futuri.'
      : 'Modifica il modello per sistemarli — lo troverai in libreria quando vorrai creare un progetto.';
    if (result.warnings && result.warnings.length > 0) {
      const warningsHtml = result.warnings.map((w) => `<li>${w}</li>`).join('');
      await Swal.fire({
        title: 'Conversione completata!',
        html: `
          <div class="publisher-import-success">
            <p class="publisher-import-success-message">
              Il tuo file è diventato un modello Giotto con ${pagesLabel}.
            </p>
            <div class="publisher-import-warnings">
              <div class="publisher-import-warnings-header">
                <i class="fas fa-exclamation-triangle"></i>
                <span>Qualche dettaglio da controllare</span>
              </div>
              <ul class="publisher-import-warnings-list">${warningsHtml}</ul>
            </div>
            <p class="publisher-import-success-redirect">
              ${successRedirectText}
            </p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: successActionText,
        confirmButtonColor: '#278e8e',
      });
    } else {
      await Swal.fire({
        title: 'Conversione completata!',
        text: shouldCreateProject
          ? `Il tuo file è diventato un modello Giotto con ${pagesLabel}. Apriamo un nuovo progetto da usare subito — e trovi il modello in libreria per ogni prossima volta.`
          : `Il tuo file è diventato un modello Giotto con ${pagesLabel}. Il modello è stato salvato in libreria e puoi modificarlo subito.`,
        icon: 'success',
        confirmButtonText: successActionText,
        confirmButtonColor: '#278e8e',
      });
    }

    trackEvent('Publisher Import Completed', {
      entryPoint,
      fileExt,
      fileSizeKB,
      pagesCount: result.pagesCount,
      hadWarnings: (result.warnings?.length || 0) > 0,
      warningsCount: result.warnings?.length || 0,
      formatFallbackUsed: !!selectedFormatFallback,
      selectedFormatId: selectedFormatFallback?.formatId,
      selectedFormatIsDefault: selectedFormatFallback?.isDefault,
      createProject: shouldCreateProject,
      navigationTarget: shouldCreateProject ? 'project' : 'template',
      isSuperUser,
      estimatedProgressDurationMs: conversionProgressTiming.durationMs,
      durationMs: Math.round(performance.now() - conversionStart),
    });

    if (shouldCreateProject) {
      router.navigate(`/canvas/${canvasId}`);
    } else {
      router.navigate(`/template-manager/${result.templateId}`);
    }
  } catch (err) {
    stopAiLoader();
    trackEvent('Publisher Import Failed', {
      entryPoint,
      fileExt,
      fileSizeKB,
      errorCode: err.code || 'unknown',
      createProject: shouldCreateProject,
      isSuperUser,
      estimatedProgressDurationMs: conversionProgressTiming.durationMs,
      durationMs: Math.round(performance.now() - conversionStart),
    });
    console.error('Publisher import from /start failed:', err);
    let errorMessage = 'Si è verificato un errore durante la conversione.';
    switch (err.code) {
      case 'invalid_source':
        errorMessage =
          'Il file non sembra un documento Publisher o CorelDraw valido.';
        break;
      case 'too_large':
        errorMessage = 'Il file supera il limite massimo di 100 MB.';
        break;
      case 'no_format_match':
        errorMessage = `Nessun formato corrisponde alle dimensioni del file (${formatDimensionsLabel(
          getDetectedFormatDimensions(err.details)
        )}). Contatta il supporto per creare un formato personalizzato.`;
        break;
      case 'conversion_failed':
        errorMessage =
          'La conversione del file è fallita. Verifica che il file non sia corrotto o protetto da password.';
        break;
    }
    Swal.fire({
      title: 'Errore',
      text: errorMessage,
      icon: 'error',
      confirmButtonColor: '#278e8e',
    });
  }
};
